import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getSessionToken, getUserDetails } from '../Services/services';
import { ROLES, hasRole } from './roles';

/**
 * Resolves the current user's access level for gated views (e.g. product
 * detail). Guests and pending users may search and view results, but must be
 * blocked from drilling into detail pages.
 *
 * Guests (no session) resolve immediately. Logged-in users have their role
 * confirmed against the server (getUserDetails) so a hard reload — where Redux
 * auth state is empty — still yields the correct role rather than wrongly
 * blocking an approved account.
 *
 * Returns:
 *  - checked: role resolution finished (use to avoid flashing a blocked state)
 *  - role:    resolved RBAC role
 *  - allowed: true when the user is STANDARD or above (approved account)
 *  - isGuest: true when there is no session token
 */
export function useApprovedAccess() {
  const auth = useSelector((state) => state.auth);
  const isGuest = !getSessionToken();
  const [role, setRole] = useState(isGuest ? ROLES.GUEST : (auth?.role || null));
  const [checked, setChecked] = useState(isGuest);

  useEffect(() => {
    if (!getSessionToken()) {
      setRole(ROLES.GUEST);
      setChecked(true);
      return;
    }
    let cancelled = false;
    if (auth?.role) setRole(auth.role);
    getUserDetails()
      .then((resp) => {
        if (cancelled) return;
        setRole(resp?.data?.role || auth?.role || ROLES.PENDING);
      })
      .catch(() => {
        if (cancelled) return;
        // Fall back to whatever Redux knows; if nothing, treat as pending so
        // access is denied rather than wrongly granted.
        setRole(auth?.role || ROLES.PENDING);
      })
      .finally(() => { if (!cancelled) setChecked(true); });
    return () => { cancelled = true; };
  }, [auth?.role]);

  return { checked, role, allowed: hasRole(role, ROLES.STANDARD), isGuest };
}
